import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

/**
 * Retries requests that fail due to network-level issues (not real server errors):
 * - status === 0  → browser couldn't complete the request at all
 * - TypeError      → "Load failed" (WebKit/iOS) or "Failed to fetch" (Chrome)
 *
 * Common inside Facebook's in-app browser (IAB), which throttles/kills
 * requests during navigation or when resources are constrained.
 *
 * Does NOT retry real HTTP errors (400/401/404/500...) — those are legitimate
 * server responses and retrying them won't help.
 */

const MAX_RETRIES = 2;
const BASE_DELAY_MS = 500;

function isNetworkError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    // status 0 = request never reached the server (aborted, CORS block, offline, IAB throttling)
    return error.status === 0;
  }
  return error instanceof TypeError;
}

export const NetworkRetryInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  // Only retry in browser — SSR should not block or retry timeouts on the server
  if (!isPlatformBrowser(platformId)) {
    return next(req);
  }

  return next(req).pipe(
    retryWhen(errors =>
      errors.pipe(
        mergeMap((error, attemptIndex) => {
          const attempt = attemptIndex + 1;

          if (isNetworkError(error) && attempt <= MAX_RETRIES) {
            const delay = BASE_DELAY_MS * attempt; // simple linear backoff: 500ms, 1000ms
            console.warn(`[networkRetryInterceptor] Network error on ${req.method} ${req.url}, retry ${attempt}/${MAX_RETRIES} in ${delay}ms`);
            return timer(delay);
          }

          // Not a network error, or retries exhausted — propagate the error as-is
          return throwError(() => error);
        })
      )
    )
  );
};